
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileUp, Link as LinkIcon, Loader2, AlertCircle } from "lucide-react";
import { JobFormValues } from "../../schemas/jobFormSchema";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface JobDocumentUploadProps {
  form: UseFormReturn<JobFormValues>;
  onProcess: (url: string, type: 'pdf' | 'url') => Promise<void>;
  isProcessing: boolean;
}

export const JobDocumentUpload = ({ form, onProcess, isProcessing }: JobDocumentUploadProps) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [extractionError, setExtractionError] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadedFile(file);
      setUploadStatus('uploading');
      setExtractionError(null);
      
      const filename = `${crypto.randomUUID()}-${file.name}`;
      
      const { error: uploadError, data } = await supabase.storage
        .from('job-documents')
        .upload(filename, file);
        
      if (uploadError) {
        setUploadStatus('error');
        throw uploadError;
      }
      
      const { data: { publicUrl } } = supabase.storage
        .from('job-documents')
        .getPublicUrl(filename);
        
      form.setValue('document_url', publicUrl);
      setUploadStatus('success');
      await onProcess(publicUrl, 'pdf');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      setUploadStatus('error');
      setExtractionError('Failed to extract job details from PDF. Please fill in the fields manually.');
    }
  };

  const handleUrlSubmit = async () => {
    const url = form.getValues('source_url');
    
    if (!url) {
      toast.error('Please enter a job posting URL');
      return;
    }
    
    setExtractionError(null);
    
    // Simple URL validation
    try {
      new URL(url);
      await onProcess(url, 'url');
    } catch (error) {
      setExtractionError('Failed to extract job details. Please fill in the fields manually.');
      toast.error('Please enter a valid URL');
    }
  };

  return (
    <div className="flex flex-col gap-4 mb-4">
      <div className="bg-muted/40 p-4 rounded-md text-sm">
        <p className="font-medium mb-2">AI-Powered Job Extraction</p>
        <p>Upload a PDF or paste a job URL to automatically extract job details. Supported job boards include LinkedIn, Indeed, Microsoft Careers, and most corporate career sites.</p>
      </div>
      
      <FormField
        control={form.control}
        name="source_url"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormLabel>Job Posting URL</FormLabel>
            <div className="flex gap-2">
              <FormControl>
                <Input 
                  placeholder="Paste job posting URL (e.g., https://careers.microsoft.com/...)" 
                  {...field}
                  className="flex-1"
                />
              </FormControl>
              <Button 
                type="button" 
                onClick={handleUrlSubmit}
                disabled={isProcessing || !field.value}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Extract
                  </>
                )}
              </Button>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <div>
        <FormLabel>Upload PDF</FormLabel>
        <Input
          type="file"
          accept=".pdf"
          onChange={handleFileUpload}
          className="hidden"
          id="pdf-upload"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => document.getElementById('pdf-upload')?.click()}
          disabled={isProcessing || uploadStatus === 'uploading'}
          className="w-full"
        >
          {uploadStatus === 'uploading' ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : uploadStatus === 'success' ? (
            <>
              <FileUp className="h-4 w-4 mr-2" />
              {uploadedFile ? `${uploadedFile.name} (Uploaded)` : 'Upload PDF'}
            </>
          ) : (
            <>
              <FileUp className="h-4 w-4 mr-2" />
              {uploadedFile ? uploadedFile.name : 'Upload PDF'}
            </>
          )}
        </Button>
      </div>
      
      {isProcessing && (
        <Alert className="bg-primary-foreground border-primary/20">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <AlertDescription>
            Extracting job details... This may take a few moments.
          </AlertDescription>
        </Alert>
      )}
      
      {extractionError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4 mr-2" />
          <AlertDescription>{extractionError}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
